import React from "react";
import styled from "styled-components";
import { Archive, Dispute, Unarchive, UrgentIcon } from "../../assets";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useSelector } from "react-redux";
import IndustryChips from "./IndustryChips";

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  position: relative;
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Button = styled.button`
  padding: 5px 12px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
`;

const AcceptButton = styled(Button)`
  background-color: #28a745;
  &:hover {
    background-color: #218838;
  }
`;

const RejectButton = styled(Button)`
  background-color: #dc3545;
  &:hover {
    background-color: #c82333;
  }
`;

const TableWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;

`;

const TableHead = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  border-bottom: solid 1px rgba(136, 136, 136, 0.1);
  align-self: stretch;
  box-sizing: border-box;
  padding: 8px 0px;
  flex-shrink: 0;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;

`;

const Cell = styled.div`
  display: flex;
  justify-content: ${(props) => props.align || "flex-start"};
  flex-direction: row;
  align-items: center;
  flex: ${(props) => (props.flex ? "1" : "none")};
  gap: 8px;
  width: ${(props) => props.width || "auto"};
  box-sizing: border-box;
`;

const Text = styled.span`
  color: ${(props) => props.color || "rgb(33, 37, 67)"};
  font-size: ${(props) => props.fontSize || "16px"};
  font-family: Montserrat, sans-serif;
  font-weight: ${(props) => props.fontWeight || "500"};
  line-height: 160%;
  text-align: left;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
  position: relative;
`;

const StatusTag = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  align-self: stretch;
  background: ${(props) => {
    switch (props.status) {
      case "successful":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "rewarded":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "won":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "resolved":
        return "linear-gradient(-90deg, rgba(18, 124, 78, 0.1), rgba(18, 124, 78, 0.1))";
      case "pending":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "active":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "open":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "tracking":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "verbally committed":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "proposal sent":
        return "linear-gradient(-90deg, rgba(244, 186, 52, 0.1), rgba(244, 186, 52, 0.1))";
      case "unsuccessful":
        return "linear-gradient(-90deg, rgba(240, 70, 63, 0.1), rgba(240, 70, 63, 0.1))";
      case "lost":
        return "linear-gradient(-90deg, rgba(240, 70, 63, 0.1), rgba(240, 70, 63, 0.1))";
      case "admin rejected":
        return "linear-gradient(-90deg, rgba(240, 70, 63, 0.1), rgba(240, 70, 63, 0.1))";
      case "paid":
        return "linear-gradient(-90deg, rgba(89, 84, 222, 0.1), rgba(89, 84, 222, 0.1))";
      case "lead identified":
        return "linear-gradient(-90deg, rgba(89, 84, 222, 0.1), rgba(89, 84, 222, 0.1))";
      case "opportunity":
        return "linear-gradient(-90deg, rgba(89, 84, 222, 0.1), rgba(89, 84, 222, 0.1))";
      default:
        return "transparent";
    }
  }};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 4px 8px;
  flex-shrink: 0;
`;

const StatusText = styled.span`
  color: ${(props) => {
    switch (props.status) {
      case "successful":
        return "rgb(18, 124, 78)";
      case "rewarded":
        return "rgb(18, 124, 78)";
      case "won":
        return "rgb(18, 124, 78)";
      case "resolved":
        return "rgb(18, 124, 78)";
      case "pending":
        return "rgb(244, 186, 52)";
      case "active":
        return "rgb(244, 186, 52)";
      case "open":
        return "rgb(244, 186, 52)";
      case "tracking":
        return "rgb(244, 186, 52)";
      case "verbally committed":
        return "rgb(244, 186, 52)";
      case "proposal sent":
        return "rgb(244, 186, 52)";
      case "unsuccessful":
        return "rgb(240, 70, 63)";
      case "lost":
        return "rgb(240, 70, 63)";
      case "admin rejected":
        return "rgb(240, 70, 63)";
      case "paid":
        return "rgba(89, 84, 222, 1)";
      case "lead identified":
        return "rgba(89, 84, 222, 1)";
      case "opportunity":
        return "rgba(89, 84, 222, 1)";
      default:
        return "inherit";
    }
  }};
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const UpdatedContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`;

const ViewFlex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2;
  width: 100%;
`;

const ViewText = styled.span`
  color: #6162c5;
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  line-height: 140%;
  text-align: left;
`;

const GFeildsInfo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: cover;
`;
const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  top: 125%; /* Show below the icon */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: fit-content;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, rgba(255, 255, 255, 1));
  font-family: "Montserrat", sans-serif;
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, rgba(255, 255, 255, 1))
      transparent;
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover ${TooltipText} {
    visibility: visible;
    opacity: 1;
  }
`;

const Table = ({
  headers,
  data,
  columns,
  viewClick,
  editClick,
  rejectClick,
  acceptClick,
  deleteClick,
  openStatusEditModal,
  showArchived,
  orderEditClick,
  openAction,
}) => {
  const { acceptLoading, rejectLoading } = useSelector(
    (state) => state.referrals
  );

  const renderCell = (column, item) => {
    if (
      column.key === "referral_member_status" ||
      column.key === "referral_status" ||
      column.key === "reward_status" ||
      column.key == "dispute_status"
    ) {
      return (
        <StatusTag status={item[column.key]?.toLowerCase()}>
          <StatusText status={item[column.key]?.toLowerCase()}>
            {item[column.key]}
          </StatusText>
        </StatusTag>
      );
    }

    if (column.key == "action") {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => {
            viewClick(item);
          }}
          style={{ cursor: "pointer" }}
        >
          <path
            d="M5.61317 12.4754C5.52238 12.3316 5.47698 12.2597 5.45157 12.1489C5.43248 12.0656 5.43248 11.9343 5.45157 11.851C5.47698 11.7401 5.52238 11.6682 5.61317 11.5245C6.36344 10.3365 8.59669 7.33325 12 7.33325C15.4034 7.33325 17.6366 10.3365 18.3869 11.5245C18.4777 11.6682 18.5231 11.7401 18.5485 11.851C18.5676 11.9343 18.5676 12.0656 18.5485 12.1489C18.5231 12.2597 18.4777 12.3316 18.3869 12.4754C17.6366 13.6634 15.4034 16.6666 12 16.6666C8.59669 16.6666 6.36344 13.6634 5.61317 12.4754Z"
            stroke="#3B5A6F"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
            stroke="#6162C5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );
    }
    if (column.key == "actions") {
      return (
        <>
          <ButtonContainer>
            <AcceptButton
              onClick={() => {
                acceptClick(item);
              }}
            >
              {acceptLoading ? <Spinner /> : "Accept"}
            </AcceptButton>
            <RejectButton
              onClick={() => {
                rejectClick(item);
              }}
            >
              {rejectLoading ? <Spinner /> : "Reject"}
            </RejectButton>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => {
                viewClick(item);
              }}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M5.61317 12.4754C5.52238 12.3316 5.47698 12.2597 5.45157 12.1489C5.43248 12.0656 5.43248 11.9343 5.45157 11.851C5.47698 11.7401 5.52238 11.6682 5.61317 11.5245C6.36344 10.3365 8.59669 7.33325 12 7.33325C15.4034 7.33325 17.6366 10.3365 18.3869 11.5245C18.4777 11.6682 18.5231 11.7401 18.5485 11.851C18.5676 11.9343 18.5676 12.0656 18.5485 12.1489C18.5231 12.2597 18.4777 12.3316 18.3869 12.4754C17.6366 13.6634 15.4034 16.6666 12 16.6666C8.59669 16.6666 6.36344 13.6634 5.61317 12.4754Z"
                stroke="#3B5A6F"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
                stroke="#6162C5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </ButtonContainer>
        </>
      );
    }

    if (column.key == "recommend_action") {
      return (
        <UpdatedContainer>
          <TooltipWrapper>
            <GFeildsInfo
              src={showArchived ? Unarchive : Archive}
              alt="icon"
              onClick={() => {
                editClick(item);
              }}
              style={{
                cursor: "pointer",
                width: showArchived ? 24 : 28,
                height: showArchived ? 20 : 24,
              }}
            />
            <TooltipText className="tooltip">
              {showArchived
                ? "Click here to unarchive."
                : "Click here to archive."}
            </TooltipText>
          </TooltipWrapper>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              viewClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M5.61317 12.4754C5.52238 12.3316 5.47698 12.2597 5.45157 12.1489C5.43248 12.0656 5.43248 11.9343 5.45157 11.851C5.47698 11.7401 5.52238 11.6682 5.61317 11.5245C6.36344 10.3365 8.59669 7.33325 12 7.33325C15.4034 7.33325 17.6366 10.3365 18.3869 11.5245C18.4777 11.6682 18.5231 11.7401 18.5485 11.851C18.5676 11.9343 18.5676 12.0656 18.5485 12.1489C18.5231 12.2597 18.4777 12.3316 18.3869 12.4754C17.6366 13.6634 15.4034 16.6666 12 16.6666C8.59669 16.6666 6.36344 13.6634 5.61317 12.4754Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </UpdatedContainer>
      );
    }

    if (column.key == "waitlist_action") {
      return (
        <TooltipWrapper>
          <GFeildsInfo
            src={showArchived ? Unarchive : Archive}
            alt="icon"
            onClick={() => {
              editClick(item);
            }}
            style={{
              cursor: "pointer",
              width: showArchived ? 24 : 28,
              height: showArchived ? 20 : 24,
            }}
          />
          <TooltipText className="tooltip">
            {showArchived
              ? "Click here to unarchive."
              : "Click here to archive."}
          </TooltipText>
        </TooltipWrapper>
      );
    }

    if (column.key == "payment_action") {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          onClick={() => {
            editClick(item);
          }}
          style={{ cursor: "pointer" }}
        >
          <path
            d="M7.03869 14.7656C7.13687 14.5103 7.18596 14.3826 7.27017 14.3242C7.34375 14.2731 7.43481 14.2538 7.5228 14.2706C7.6235 14.2898 7.7202 14.3865 7.9136 14.5799L9.41966 16.086C9.61306 16.2794 9.70976 16.3761 9.72898 16.4767C9.74579 16.5647 9.72647 16.6558 9.67537 16.7294C9.61691 16.8136 9.48927 16.8627 9.23399 16.9609L5.66663 18.3329L7.03869 14.7656Z"
            stroke="#3B5A6F"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7.08951 14.6334C7.18053 14.3967 7.22603 14.2784 7.28545 14.1677C7.33823 14.0694 7.39914 13.9757 7.46755 13.8875C7.54457 13.7883 7.63421 13.6986 7.81348 13.5194L15.3333 5.99955C16.0697 5.26317 17.2636 5.26317 18 5.99955C18.7364 6.73593 18.7364 7.92984 18 8.66622L10.4801 16.186C10.3009 16.3653 10.2112 16.4549 10.112 16.532C10.0238 16.6004 9.93009 16.6613 9.83177 16.7141C9.72108 16.7735 9.60277 16.819 9.36614 16.91L5.66663 18.3329L7.08951 14.6334Z"
            stroke="#6162C5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );
    }

    if (column.key == "faq_last_updated") {
      return (
        <UpdatedContainer>
          <Text color="#64748B">{item[column.key]}</Text>
          <ViewText
            onClick={() => {
              viewClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            View History
          </ViewText>
        </UpdatedContainer>
      );
    }
    if (column.key == "payoutrequests_action") {
      return (
        <UpdatedContainer>
          <Text color="#64748B">{item[column.key]}</Text>
          <ViewText
            onClick={() => {
              viewClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            View History
          </ViewText>
        </UpdatedContainer>
      );
    }
    if (column.key == "charitypayouts_action") {
      return (
        <UpdatedContainer>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              editClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M7.03869 14.7665C7.13687 14.5113 7.18596 14.3836 7.27017 14.3252C7.34375 14.2741 7.43481 14.2547 7.5228 14.2715C7.6235 14.2908 7.7202 14.3875 7.9136 14.5809L9.41966 16.0869C9.61306 16.2803 9.70976 16.377 9.72898 16.4777C9.74579 16.5657 9.72647 16.6568 9.67537 16.7304C9.61691 16.8146 9.48927 16.8637 9.23399 16.9618L5.66663 18.3339L7.03869 14.7665Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.08951 14.6334C7.18053 14.3967 7.22603 14.2784 7.28545 14.1677C7.33823 14.0694 7.39914 13.9757 7.46755 13.8875C7.54457 13.7883 7.63421 13.6986 7.81348 13.5194L15.3333 5.99955C16.0697 5.26317 17.2636 5.26317 18 5.99955C18.7364 6.73593 18.7364 7.92984 18 8.66622L10.4801 16.186C10.3009 16.3653 10.2112 16.4549 10.112 16.532C10.0238 16.6004 9.93009 16.6613 9.83177 16.7141C9.72108 16.7735 9.60277 16.819 9.36614 16.91L5.66663 18.3329L7.08951 14.6334Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </UpdatedContainer>
      );
    }

    if (column.key == "faq_action") {
      return (
        <UpdatedContainer>
          {/* <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              editClick("Edit", item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M7.03869 14.7665C7.13687 14.5113 7.18596 14.3836 7.27017 14.3252C7.34375 14.2741 7.43481 14.2547 7.5228 14.2715C7.6235 14.2908 7.7202 14.3875 7.9136 14.5809L9.41966 16.0869C9.61306 16.2803 9.70976 16.377 9.72898 16.4777C9.74579 16.5657 9.72647 16.6568 9.67537 16.7304C9.61691 16.8146 9.48927 16.8637 9.23399 16.9618L5.66663 18.3339L7.03869 14.7665Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.08951 14.6334C7.18053 14.3967 7.22603 14.2784 7.28545 14.1677C7.33823 14.0694 7.39914 13.9757 7.46755 13.8875C7.54457 13.7883 7.63421 13.6986 7.81348 13.5194L15.3333 5.99955C16.0697 5.26317 17.2636 5.26317 18 5.99955C18.7364 6.73593 18.7364 7.92984 18 8.66622L10.4801 16.186C10.3009 16.3653 10.2112 16.4549 10.112 16.532C10.0238 16.6004 9.93009 16.6613 9.83177 16.7141C9.72108 16.7735 9.60277 16.819 9.36614 16.91L5.66663 18.3329L7.08951 14.6334Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg> */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              deleteClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M14.6667 8.00065V7.46732C14.6667 6.72058 14.6667 6.34721 14.5214 6.062C14.3936 5.81111 14.1896 5.60714 13.9387 5.47931C13.6535 5.33398 13.2801 5.33398 12.5334 5.33398H11.4667C10.72 5.33398 10.3466 5.33398 10.0614 5.47931C9.8105 5.60714 9.60653 5.81111 9.4787 6.062C9.33337 6.34721 9.33337 6.72058 9.33337 7.46732V8.00065M10.6667 11.6673V15.0007M13.3334 11.6673V15.0007"
              stroke="#F0463F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6 8H18M16.6667 8V15.4667C16.6667 16.5868 16.6667 17.1468 16.4487 17.5746C16.2569 17.951 15.951 18.2569 15.5746 18.4487C15.1468 18.6667 14.5868 18.6667 13.4667 18.6667H10.5333C9.41323 18.6667 8.85318 18.6667 8.42535 18.4487C8.04903 18.2569 7.74307 17.951 7.55132 17.5746C7.33333 17.1468 7.33333 16.5868 7.33333 15.4667V8"
              stroke="#F0463F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </UpdatedContainer>
      );
    }
    if (column.key == "charity_action") {
      return (
        <UpdatedContainer>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              editClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M7.03869 14.7665C7.13687 14.5113 7.18596 14.3836 7.27017 14.3252C7.34375 14.2741 7.43481 14.2547 7.5228 14.2715C7.6235 14.2908 7.7202 14.3875 7.9136 14.5809L9.41966 16.0869C9.61306 16.2803 9.70976 16.377 9.72898 16.4777C9.74579 16.5657 9.72647 16.6568 9.67537 16.7304C9.61691 16.8146 9.48927 16.8637 9.23399 16.9618L5.66663 18.3339L7.03869 14.7665Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7.08951 14.6334C7.18053 14.3967 7.22603 14.2784 7.28545 14.1677C7.33823 14.0694 7.39914 13.9757 7.46755 13.8875C7.54457 13.7883 7.63421 13.6986 7.81348 13.5194L15.3333 5.99955C16.0697 5.26317 17.2636 5.26317 18 5.99955C18.7364 6.73593 18.7364 7.92984 18 8.66622L10.4801 16.186C10.3009 16.3653 10.2112 16.4549 10.112 16.532C10.0238 16.6004 9.93009 16.6613 9.83177 16.7141C9.72108 16.7735 9.60277 16.819 9.36614 16.91L5.66663 18.3329L7.08951 14.6334Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              viewClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M1.61317 8.47562C1.52238 8.33186 1.47698 8.25998 1.45157 8.14911C1.43248 8.06583 1.43248 7.9345 1.45157 7.85122C1.47698 7.74035 1.52238 7.66847 1.61317 7.52471C2.36344 6.33672 4.59669 3.3335 8.00002 3.3335C11.4034 3.3335 13.6366 6.33672 14.3869 7.52471C14.4777 7.66847 14.5231 7.74035 14.5485 7.85122C14.5676 7.9345 14.5676 8.06583 14.5485 8.14911C14.5231 8.25998 14.4777 8.33186 14.3869 8.47562C13.6366 9.6636 11.4034 12.6668 8.00002 12.6668C4.59669 12.6668 2.36344 9.6636 1.61317 8.47562Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              deleteClick(item);
            }}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M14.6667 8.00065V7.46732C14.6667 6.72058 14.6667 6.34721 14.5214 6.062C14.3936 5.81111 14.1896 5.60714 13.9387 5.47931C13.6535 5.33398 13.2801 5.33398 12.5334 5.33398H11.4667C10.72 5.33398 10.3466 5.33398 10.0614 5.47931C9.8105 5.60714 9.60653 5.81111 9.4787 6.062C9.33337 6.34721 9.33337 6.72058 9.33337 7.46732V8.00065M10.6667 11.6673V15.0007M13.3334 11.6673V15.0007"
              stroke="#F0463F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6 8H18M16.6667 8V15.4667C16.6667 16.5868 16.6667 17.1468 16.4487 17.5746C16.2569 17.951 15.951 18.2569 15.5746 18.4487C15.1468 18.6667 14.5868 18.6667 13.4667 18.6667H10.5333C9.41323 18.6667 8.85318 18.6667 8.42535 18.4487C8.04903 18.2569 7.74307 17.951 7.55132 17.5746C7.33333 17.1468 7.33333 16.5868 7.33333 15.4667V8"
              stroke="#F0463F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </UpdatedContainer>
      );
    }

    if (column.key == "badge") {
      return item[column.key] == "New" ? (
        <svg
          width="68"
          height="24"
          viewBox="0 0 68 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M59.3333 4H8.33325V20H59.3333L53.3333 12L59.3333 4Z"
            fill="#76C065"
          />
          <path
            d="M15.3673 16V8.3H16.5443L21.3733 14.229H20.7903V8.3H22.2093V16H21.0323L16.2033 10.071H16.7863V16H15.3673ZM25.5925 11.501H29.4205V12.678H25.5925V11.501ZM25.7025 14.801H30.0475V16H24.2725V8.3H29.8935V9.499H25.7025V14.801ZM33.3492 16L30.7972 8.3H32.2822L34.5592 15.263H33.8222L36.1872 8.3H37.5072L39.8062 15.263H39.0912L41.4232 8.3H42.7872L40.2352 16H38.7172L36.6162 9.719H37.0122L34.8782 16H33.3492Z"
            fill="white"
          />
          <path
            d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
            fill="#76C065"
          />
          <path
            d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
            fill="#4C9F39"
          />
        </svg>
      ) : item[column.key] == "Popular" ? (
        <svg
          width="84"
          height="24"
          viewBox="0 0 84 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M70.3333 4H8.33325V20H70.3333V4Z"
            fill="#FD4D93"
          />
          <path
            d="M15.3673 16V8.3H18.5353C19.2173 8.3 19.8003 8.41 20.2843 8.63C20.7756 8.85 21.1533 9.16533 21.4173 9.576C21.6813 9.98667 21.8133 10.4743 21.8133 11.039C21.8133 11.6037 21.6813 12.0913 21.4173 12.502C21.1533 12.9127 20.7756 13.228 20.2843 13.448C19.8003 13.668 19.2173 13.778 18.5353 13.778H16.1593L16.7973 13.107V16H15.3673ZM16.7973 13.261L16.1593 12.568H18.4693C19.0999 12.568 19.5729 12.436 19.8883 12.172C20.2109 11.9007 20.3723 11.523 20.3723 11.039C20.3723 10.5477 20.2109 10.17 19.8883 9.906C19.5729 9.642 19.0999 9.51 18.4693 9.51H16.1593L16.7973 8.806V13.261ZM25.7278 16.077C25.1265 16.077 24.5911 15.9487 24.1218 15.692C23.6525 15.428 23.2821 15.0687 23.0108 14.614C22.7395 14.1593 22.6038 13.6423 22.6038 13.063C22.6038 12.4763 22.7395 11.9593 23.0108 11.512C23.2821 11.0573 23.6525 10.7017 24.1218 10.445C24.5911 10.1883 25.1265 10.06 25.7278 10.06C26.3365 10.06 26.8755 10.1883 27.3448 10.445C27.8215 10.7017 28.1918 11.0537 28.4558 11.501C28.7271 11.9483 28.8628 12.469 28.8628 13.063C28.8628 13.6423 28.7271 14.1593 28.4558 14.614C28.1918 15.0687 27.8215 15.428 27.3448 15.692C26.8755 15.9487 26.3365 16.077 25.7278 16.077ZM25.7278 14.9C26.0651 14.9 26.3658 14.8267 26.6298 14.68C26.8938 14.5333 27.0991 14.3207 27.2458 14.042C27.3998 13.7633 27.4768 13.437 27.4768 13.063C27.4768 12.6817 27.3998 12.3553 27.2458 12.084C27.0991 11.8053 26.8938 11.5927 26.6298 11.446C26.3658 11.2993 26.0688 11.226 25.7388 11.226C25.4015 11.226 25.1008 11.2993 24.8368 11.446C24.5801 11.5927 24.3748 11.8053 24.2208 12.084C24.0668 12.3553 23.9898 12.6817 23.9898 13.063C23.9898 13.437 24.0668 13.7633 24.2208 14.042C24.3748 14.3207 24.5801 14.5333 24.8368 14.68C25.1008 14.8267 25.3978 14.9 25.7278 14.9ZM33.4336 16.077C32.957 16.077 32.5206 15.967 32.1246 15.747C31.736 15.527 31.4243 15.197 31.1896 14.757C30.9623 14.3097 30.8486 13.745 30.8486 13.063C30.8486 12.3737 30.9586 11.809 31.1786 11.369C31.406 10.929 31.714 10.6027 32.1026 10.39C32.4913 10.17 32.935 10.06 33.4336 10.06C34.013 10.06 34.5226 10.1847 34.9626 10.434C35.41 10.6833 35.762 11.0317 36.0186 11.479C36.2826 11.9263 36.4146 12.4543 36.4146 13.063C36.4146 13.6717 36.2826 14.2033 36.0186 14.658C35.762 15.1053 35.41 15.4537 34.9626 15.703C34.5226 15.9523 34.013 16.077 33.4336 16.077ZM30.1776 18.134V10.126H31.4866V11.512L31.4426 13.074L31.5526 14.636V18.134H30.1776ZM33.2796 14.9C33.6096 14.9 33.903 14.8267 34.1596 14.68C34.4236 14.5333 34.6326 14.3207 34.7866 14.042C34.9406 13.7633 35.0176 13.437 35.0176 13.063C35.0176 12.6817 34.9406 12.3553 34.7866 12.084C34.6326 11.8053 34.4236 11.5927 34.1596 11.446C33.903 11.2993 33.6096 11.226 33.2796 11.226C32.9496 11.226 32.6526 11.2993 32.3886 11.446C32.1246 11.5927 31.9156 11.8053 31.7616 12.084C31.6076 12.3553 31.5306 12.6817 31.5306 13.063C31.5306 13.437 31.6076 13.7633 31.7616 14.042C31.9156 14.3207 32.1246 14.5333 32.3886 14.68C32.6526 14.8267 32.9496 14.9 33.2796 14.9ZM40.2047 16.077C39.706 16.077 39.266 15.9853 38.8847 15.802C38.5033 15.6113 38.2063 15.3253 37.9937 14.944C37.781 14.5553 37.6747 14.0677 37.6747 13.481V10.126H39.0497V13.294C39.0497 13.822 39.167 14.218 39.4017 14.482C39.6437 14.7387 39.9847 14.867 40.4247 14.867C40.7473 14.867 41.026 14.801 41.2607 14.669C41.5027 14.537 41.6897 14.339 41.8217 14.075C41.961 13.811 42.0307 13.4847 42.0307 13.096V10.126H43.4057V16H42.0967V14.416L42.3277 14.9C42.1297 15.2813 41.84 15.5747 41.4587 15.78C41.0773 15.978 40.6593 16.077 40.2047 16.077ZM45.206 16V7.838H46.581V16H45.206ZM52.0046 16V14.812L51.9276 14.559V12.48C51.9276 12.0767 51.8066 11.765 51.5646 11.545C51.3226 11.3177 50.956 11.204 50.4646 11.204C50.1346 11.204 49.8083 11.2553 49.4856 11.358C49.1703 11.4607 48.9026 11.6037 48.6826 11.787L48.1436 10.786C48.459 10.544 48.833 10.3643 49.2656 10.247C49.7056 10.1223 50.1603 10.06 50.6296 10.06C51.4803 10.06 52.1366 10.2653 52.5986 10.676C53.068 11.0793 53.3026 11.7063 53.3026 12.557V16H52.0046ZM50.1566 16.077C49.7166 16.077 49.3316 16.0037 49.0016 15.857C48.6716 15.703 48.415 15.494 48.2316 15.23C48.0556 14.9587 47.9676 14.6543 47.9676 14.317C47.9676 13.987 48.0446 13.69 48.1986 13.426C48.36 13.162 48.6203 12.953 48.9796 12.799C49.339 12.645 49.8156 12.568 50.4096 12.568H52.1146V13.481H50.5086C50.0393 13.481 49.724 13.558 49.5626 13.712C49.4013 13.8587 49.3206 14.042 49.3206 14.262C49.3206 14.5113 49.4196 14.7093 49.6176 14.856C49.8156 15.0027 50.0906 15.076 50.4426 15.076C50.78 15.076 51.0806 14.999 51.3446 14.845C51.616 14.691 51.8103 14.4637 51.9276 14.163L52.1586 14.988C52.0266 15.3327 51.7883 15.6003 51.4436 15.791C51.1063 15.9817 50.6773 16.077 50.1566 16.077ZM55.0673 16V10.126H56.3763V11.743L56.2223 11.27C56.3983 10.874 56.6733 10.5733 57.0473 10.368C57.4286 10.1627 57.9016 10.06 58.4663 10.06V11.369C58.4076 11.3543 58.3526 11.347 58.3013 11.347C58.25 11.3397 58.1986 11.336 58.1473 11.336C57.6266 11.336 57.2123 11.49 56.9043 11.798C56.5963 12.0987 56.4423 12.5497 56.4423 13.151V16H55.0673Z"
            fill="white"
          />
          <path
            d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
            fill="#E3387D"
          />
          <path
            d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
            fill="#FD4D93"
          />
        </svg>
      ) : item[column.key] == "Ending" ? (
        <svg
          width="80"
          height="24"
          viewBox="0 0 80 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M69.3333 4H8.33325V20H69.3333V4Z"
            fill="#FAAE5D"
          />
          <path
            d="M16.6873 11.501H20.5153V12.678H16.6873V11.501ZM16.7973 14.801H21.1423V16H15.3673V8.3H20.9883V9.499H16.7973V14.801ZM25.9374 10.06C26.4067 10.06 26.8247 10.1517 27.1914 10.335C27.5654 10.5183 27.8587 10.8007 28.0714 11.182C28.2841 11.556 28.3904 12.04 28.3904 12.634V16H27.0154V12.81C27.0154 12.2893 26.8907 11.9007 26.6414 11.644C26.3994 11.3873 26.0584 11.259 25.6184 11.259C25.2957 11.259 25.0097 11.325 24.7604 11.457C24.5111 11.589 24.3167 11.787 24.1774 12.051C24.0454 12.3077 23.9794 12.634 23.9794 13.03V16H22.6044V10.126H23.9134V11.71L23.6824 11.226C23.8877 10.852 24.1847 10.566 24.5734 10.368C24.9694 10.1627 25.4241 10.06 25.9374 10.06ZM32.6314 16.077C32.0667 16.077 31.5571 15.9523 31.1024 15.703C30.6551 15.4463 30.3031 15.0943 30.0464 14.647C29.7897 14.1997 29.6614 13.6717 29.6614 13.063C29.6614 12.4543 29.7897 11.9263 30.0464 11.479C30.3031 11.0317 30.6551 10.6833 31.1024 10.434C31.5571 10.1847 32.0667 10.06 32.6314 10.06C33.1227 10.06 33.5627 10.17 33.9514 10.39C34.3401 10.6027 34.6481 10.929 34.8754 11.369C35.1027 11.809 35.2164 12.3737 35.2164 13.063C35.2164 13.745 35.1064 14.3097 34.8864 14.757C34.6664 15.197 34.3621 15.527 33.9734 15.747C33.5847 15.967 33.1374 16.077 32.6314 16.077ZM32.7964 14.9C33.1264 14.9 33.4197 14.8267 33.6764 14.68C33.9404 14.5333 34.1494 14.3207 34.3034 14.042C34.4647 13.7633 34.5454 13.437 34.5454 13.063C34.5454 12.6817 34.4647 12.3553 34.3034 12.084C34.1494 11.8053 33.9404 11.5927 33.6764 11.446C33.4197 11.2993 33.1264 11.226 32.7964 11.226C32.4664 11.226 32.1694 11.2993 31.9054 11.446C31.6487 11.5927 31.4397 11.8053 31.2784 12.084C31.1244 12.3553 31.0474 12.6817 31.0474 13.063C31.0474 13.437 31.1244 13.7633 31.2784 14.042C31.4397 14.3207 31.6487 14.5333 31.9054 14.68C32.1694 14.8267 32.4664 14.9 32.7964 14.9ZM34.5784 16V14.614L34.6334 13.052L34.5234 11.49V7.838H35.8874V16H34.5784ZM37.6972 16V10.126H39.0722V16H37.6972ZM38.3902 9.158C38.1335 9.158 37.9208 9.07733 37.7522 8.916C37.5908 8.75467 37.5102 8.56033 37.5102 8.333C37.5102 8.09833 37.5908 7.904 37.7522 7.75C37.9208 7.58867 38.1335 7.508 38.3902 7.508C38.6468 7.508 38.8558 7.585 39.0172 7.739C39.1858 7.88567 39.2702 8.07267 39.2702 8.3C39.2702 8.542 39.1895 8.74733 39.0282 8.916C38.8668 9.07733 38.6542 9.158 38.3902 9.158ZM44.2099 10.06C44.6792 10.06 45.0972 10.1517 45.4639 10.335C45.8379 10.5183 46.1312 10.8007 46.3439 11.182C46.5565 11.556 46.6629 12.04 46.6629 12.634V16H45.2879V12.81C45.2879 12.2893 45.1632 11.9007 44.9139 11.644C44.6719 11.3873 44.3309 11.259 43.8909 11.259C43.5682 11.259 43.2822 11.325 43.0329 11.457C42.7835 11.589 42.5892 11.787 42.4499 12.051C42.3179 12.3077 42.2519 12.634 42.2519 13.03V16H40.8769V10.126H42.1859V11.71L41.9549 11.226C42.1602 10.852 42.4572 10.566 42.8459 10.368C43.2419 10.1627 43.6965 10.06 44.2099 10.06ZM51.0799 18.211C50.5372 18.211 50.0055 18.1377 49.4849 17.991C48.9715 17.8517 48.5499 17.6463 48.2199 17.375L48.8359 16.341C49.0925 16.5537 49.4152 16.7223 49.8039 16.847C50.1999 16.979 50.5995 17.045 51.0029 17.045C51.6482 17.045 52.1212 16.8947 52.4219 16.594C52.7225 16.3007 52.8729 15.857 52.8729 15.263V14.218L52.9829 12.887L52.9389 11.556V10.126H54.2479V15.109C54.2479 16.1723 53.9765 16.9533 53.4339 17.452C52.8912 17.958 52.1065 18.211 51.0799 18.211ZM50.9039 15.736C50.3465 15.736 49.8405 15.6187 49.3859 15.384C48.9385 15.142 48.5829 14.8083 48.3189 14.383C48.0622 13.9577 47.9339 13.459 47.9339 12.887C47.9339 12.3223 48.0622 11.8273 48.3189 11.402C48.5829 10.9767 48.9385 10.6467 49.3859 10.412C49.8405 10.1773 50.3465 10.06 50.9039 10.06C51.4025 10.06 51.8499 10.159 52.2459 10.357C52.6419 10.555 52.9572 10.863 53.1919 11.281C53.4339 11.699 53.5549 12.2343 53.5549 12.887C53.5549 13.5397 53.4339 14.0787 53.1919 14.504C52.9572 14.922 52.6419 15.2337 52.2459 15.439C51.8499 15.637 51.4025 15.736 50.9039 15.736ZM51.1129 14.57C51.4575 14.57 51.7655 14.5003 52.0369 14.361C52.3082 14.2143 52.5172 14.0163 52.6639 13.767C52.8179 13.5103 52.8949 13.217 52.8949 12.887C52.8949 12.557 52.8179 12.2673 52.6639 12.018C52.5172 11.7613 52.3082 11.567 52.0369 11.435C51.7655 11.2957 51.4575 11.226 51.1129 11.226C50.7682 11.226 50.4565 11.2957 50.1779 11.435C49.9065 11.567 49.6939 11.7613 49.5399 12.018C49.3932 12.2673 49.3199 12.557 49.3199 12.887C49.3199 13.217 49.3932 13.5103 49.5399 13.767C49.6939 14.0163 49.9065 14.2143 50.1779 14.361C50.4565 14.5003 50.7682 14.57 51.1129 14.57Z"
            fill="white"
          />
          <path
            d="M4.33325 6C4.33325 4.89543 5.22868 4 6.33325 4H8.33325V20H4.33325V6Z"
            fill="#FAAE5D"
          />
          <path
            d="M4.33325 20L10.3333 20L10.3333 24L4.33325 20Z"
            fill="#DD8F3C"
          />
        </svg>
      ) : (
        <></>
      );
    }
    if (column.key == "industry_sector") {
      return (
        <Text
          color={column.color}
          fontSize={column.fontSize}
          fontWeight={item.type == "Parent" ? "700" : column.fontWeight}
          style={{ marginLeft: item.type == "Child" ? "16px" : "0px" }}
        >
          {item[column.key]}
        </Text>
      );
    }

    if (column.key == "referral_id") {
      return (
        <ViewFlex>
          <Text
            color={column.color}
            fontSize={column.fontSize}
            fontWeight={column.fontWeight}
          >
            {item[column.key]}
          </Text>
          {item.dispute && item.dispute.dispute_status === "Open" && (
            <TooltipWrapper>
              <GFeildsInfo src={Dispute} alt="icon" />
              <TooltipText>This referral has a dispute.</TooltipText>
            </TooltipWrapper>
          )}
          {item.is_urgent && (
            <TooltipWrapper>
              <GFeildsInfo src={UrgentIcon} alt="icon" />
              <TooltipText>Urgent: time-sensitive referral. Quick response requested.</TooltipText>
            </TooltipWrapper>
          )}
        </ViewFlex>
      );
    }
    if (column.key === "donation_status" || column.key === "payout_status") {
      const status = item[column.key]
        ? item[column.key].charAt(0)?.toUpperCase() +
          item[column.key].slice(1)?.toLowerCase()
        : "";

      // Determine badge colors based on status
      const getBadgeStyle = (status) => {
        switch (status) {
          case "Donated":
            return { bg: "rgba(18,124,78,0.10)", color: "#127C4E" };
          case "Pending":
            return { bg: "rgba(244, 186, 52, 0.10)", color: "#F4bA34" };
          case "Paid":
            return { bg: "rgba(18,124,78,0.10)", color: "#127C4E" };
          case "Unpaid":
            return { bg: "rgba(244, 186, 52, 0.10)", color: "#F4bA34" };
          case "in_request":
            return { bg: "rgba(244, 186, 52, 0.10)", color: "#F4bA34" };
          default:
            return { bg: "#F0F0F0", color: "#333" };
        }
      };

      const { bg, color } = getBadgeStyle(status);

      return (
        <StatusBadge bg={bg} color={color}>
          {status}
        </StatusBadge>
      );
    }

    if (column.key == "reward") {
      return (
        <Text
          color={column.color}
          fontSize={column.fontSize}
          fontWeight={column.fontWeight}
        >
          £{item[column.key]}
        </Text>
      );
    }
    if (column.key == "successfulReferrals") {
      const status = item[column.key]
        ? item[column.key].charAt(0).toUpperCase() +
          item[column.key].slice(1)?.toLowerCase()
        : "";

      // Determine badge colors based on status
      const getBadgeStyle = (status) => {
        switch (status) {
          case "Successful referral":
            return { bg: "rgba(18,124,78,0.10)", color: "#127C4E" };
          case "Signed up":
            return { bg: "rgba(244, 186, 52, 0.10)", color: "#F4bA34" };

          default:
            return { bg: "#F0F0F0", color: "#333" };
        }
      };

      const { bg, color } = getBadgeStyle(status);

      return (
        <StatusBadge bg={bg} color={color} width={"fit-content"}>
          {status}
        </StatusBadge>
      );
    }

    if (column.key === "status") {
      const statusValue = item[column.key]?.toLowerCase?.();
      const textColor = statusColorMap[statusValue] || "#334155"; // default: slate-700

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <StatusText style={{ color: textColor }}>
            {item[column.key]}
          </StatusText>
          <svg
            cursor={"pointer"}
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => {
              editClick(item);
            }}
          >
            <path
              d="M3.03869 10.7665C3.13687 10.5113 3.18596 10.3836 3.27017 10.3252C3.34375 10.2741 3.43481 10.2547 3.5228 10.2715C3.6235 10.2908 3.7202 10.3875 3.9136 10.5809L5.41966 12.0869C5.61306 12.2803 5.70976 12.377 5.72898 12.4777C5.74579 12.5657 5.72647 12.6568 5.67537 12.7304C5.61691 12.8146 5.48927 12.8637 5.23399 12.9618L1.66663 14.3339L3.03869 10.7665Z"
              stroke="#3B5A6F"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M3.08951 10.6334C3.18053 10.3967 3.22603 10.2784 3.28545 10.1677C3.33823 10.0694 3.39914 9.97569 3.46755 9.88752C3.54457 9.78827 3.63421 9.69864 3.81348 9.51936L11.3333 1.99955C12.0697 1.26317 13.2636 1.26317 14 1.99955C14.7364 2.73593 14.7364 3.92984 14 4.66622L6.48015 12.186C6.30088 12.3653 6.21124 12.4549 6.11199 12.532C6.02383 12.6004 5.93009 12.6613 5.83177 12.7141C5.72108 12.7735 5.60277 12.819 5.36614 12.91L1.66663 14.3329L3.08951 10.6334Z"
              stroke="#6162C5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      );
    }

    if (column.key == "order") {
      return (
        <>
          {item["ordering"] == "first" ? (
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => {
                orderEditClick("down", item);
              }}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M8.5 10L12.5 14L16.5 10"
                stroke="#6162C5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          ) : item["ordering"] == "last" ? (
            <svg
              width="25"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => {
                orderEditClick("up", item);
              }}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M16.5 14L12.5 10L8.5 14"
                stroke="#6162C5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          ) : (
            <div style={{ display: "flex", flexDirection: "row" }}>
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  orderEditClick("up", item);
                }}
                style={{ cursor: "pointer" }}
              >
                <path
                  d="M16.5 14L12.5 10L8.5 14"
                  stroke="#6162C5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => {
                  orderEditClick("down", item);
                }}
                style={{ cursor: "pointer" }}
              >
                <path
                  d="M8.5 10L12.5 14L16.5 10"
                  stroke="#6162C5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          )}
        </>
      );
    }

    if (column.key === "industry") {
      const industries = item[column.key];
      if (Array.isArray(industries)) {
        return <IndustryChips industries={industries} maxDisplay={3} />;
      }
    }

    return (
      <Text
        color={column.color}
        fontSize={column.fontSize}
        fontWeight={column.fontWeight}
        data-tooltip={item[column.key]}
      >
        {item[column.key]}
      </Text>
    );
  };

  return (
    <TableWrapper>
      <TableHead>
        {headers.map((header, index) => (
          <Cell
            key={index}
            width={header.width}
            flex={header.flex}
            align={header.align}
          >
            <Text color="rgb(100, 116, 139)">{header.label}</Text>
          </Cell>
        ))}
      </TableHead>
      {data.map((item, rowIndex) => (
        <TableRow key={rowIndex}>
          {columns.map((column, colIndex) => (
            <Cell
              key={colIndex}
              width={column.width}
              flex={column.flex}
              align={column.align}
            >
              {renderCell(column, item)}
            </Cell>
          ))}
        </TableRow>
      ))}
    </TableWrapper>
  );
};

export default Table;

const statusColorMap = {
  active: "#10B981",
  suspended: "#EF4444",
  freeze: "#8B5CF6",
};

export const StatusBadge = styled.div`
  display: flex;
  width: ${(props) => props.width || "96px"};
  padding: var(--Padding-Tiny, 4px) var(--Padding-Small, 8px);
  justify-content: center;
  align-items: center;
  gap: var(--Padding-Tiny, 4px);
  border-radius: 4px;
  background: ${(props) =>
    props.bg ||
    "var(--Color-mics-acent-color-2---Light, rgba(18,124,78,0.10))"};
  color: ${(props) => props.color || "rgba(18, 124, 78, 1)"};
  font-weight: 600;
  font-size: 14px;
  font-family: Montserrat;
`;
